import React from 'react';
import { FlatList, ImageSourcePropType, Modal, Pressable, View } from 'react-native';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { applicationPages, Module, ModulePage, RootNavigation } from '../Routes';
import MyAppText from './MyAppText';
import { translate } from '../services/translate';
import TouchableOpacityWithMiddleClick from './TouchableOpacityWithMiddleClick';
import { centralAPI, NotificationType, UserNotification } from '../services/central-api';
import moment from 'moment';
import { Hoverable } from 'react-native-web-hover';
import { useAuth } from '../context/Auth';
import { CurrentApp, useCurrentApp } from '../context/CurrentApp';
import { WindowInformation } from '../services/window-information';
import { QueryObserverResult } from '@tanstack/react-query';
import { TranslateRich } from './TranslateRich';
import { ModuleParamLists, RootScreensParamLists } from '../typings/Params';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';

type ModuleDestination = {
    [M in keyof ModuleParamLists]: {
        [S in keyof ModuleParamLists[M]]: {
            module: M;
            screen: S;
            params?: ModuleParamLists[M][S];
        };
    }[keyof ModuleParamLists[M]];
}[keyof ModuleParamLists];

type RootDestination = {
    [S in keyof RootScreensParamLists]: {
        rootScreen: S;
        params?: RootScreensParamLists[S];
    };
}[keyof RootScreensParamLists];

type NotificationDestination = ModuleDestination | RootDestination;

type NotificationDefinition = {
    text: (notification: UserNotification) => string | React.JSX.Element;
    destination: (
        notification: UserNotification,
        user: AuthenticatedUser | null | undefined,
    ) => NotificationDestination;
};

const notifications: Record<NotificationType, NotificationDefinition> = {
    download_ready: {
        text: (notification) => <TranslateRich
            i18nKey={'download_ready'}
            values={{
                downloadName: <strong>{notification.reference.name}</strong>,
            }}
        />,
        destination: (notification, user) => ({
            module: 'Cameras',
            screen: 'Downloads',
            params: {
                page: 1,
                limit: 25,
                userId: user?.id,
                textFilter: notification.reference.name,
            },
        }),
    },
    maintenance_request_to_review: {
        text: (notification) => <TranslateRich
            i18nKey={'maintenance_request_to_review'}
            values={{
                maintenanceRequest: <strong>#{notification.reference.id}</strong>,
            }}
        />,
        destination: (notification) => ({
            module: 'User',
            screen: 'GetOwnMaintenanceRequest',
            params: {
                id: Number(notification.reference.id),
            },
        }),
    },
};

export function filterAccessibleNotifications(
    items: UserNotification[],
    user: AuthenticatedUser | null | undefined,
    currentApp: CurrentApp,
) {
    return items.filter(item => {
        const definition = notifications[item.type];
        if (!definition) {
            return false;
        }

        const destination = definition.destination(item, user);
        if ('rootScreen' in destination) {
            return applicationPages.screens[destination.rootScreen]
                .canAccess(user, currentApp);
        }

        // The module and screen names are correlated in NotificationDestination;
        // this cast is only needed for indexed lookup through the heterogeneous route config.
        const module = applicationPages.modules[destination.module] as Module<keyof ModuleParamLists>;
        const screen = (module.screens as Record<string, ModulePage<keyof ModuleParamLists, never>>)[destination.screen];

        return module.canAccess(user, currentApp) && screen.canAccess(user, currentApp);
    });
}

function getNotificationIcon(
    item: UserNotification,
    user: AuthenticatedUser | null | undefined,
): ImageSourcePropType | undefined {
    const destination = notifications[item.type].destination(item, user);
    if ('rootScreen' in destination) {
        return undefined;
    }

    const module = applicationPages.modules[destination.module];
    return 'icon' in module ? module.icon : undefined;
}

function navigateToDestination(destination: NotificationDestination, navigation: RootNavigation) {
    if ('rootScreen' in destination) {
        navigation.navigate(destination.rootScreen, destination.params as never);
        return;
    }

    navigation.navigate(destination.module, {
        screen: destination.screen,
        params: destination.params as never,
    } as never);
}

interface NotificationModalParameters {
    navigation: RootNavigation;
    isModalVisible: boolean;
    setIsModalVisible: (value: boolean) => void;
    notifications: UserNotification[];
    refetch: () => Promise<QueryObserverResult<{
        notifications: UserNotification[];
    }, unknown>>
    ;
}

function NotificationCard({ item, navigation, setIsModalVisible, refetch }: {
    item: UserNotification;
    navigation: RootNavigation;
    setIsModalVisible: (value: boolean) => void;
    refetch: () => Promise<QueryObserverResult<{
        notifications: UserNotification[];
    }, unknown>>;
}) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticateUser } = useAuth();
    const currentApp = useCurrentApp();

    const notification = notifications[item.type];

    if (!notification || !filterAccessibleNotifications([item], authenticateUser, currentApp).length) {
        return <></>;
    }

    const destination = notification.destination(item, authenticateUser);
    const icon = getNotificationIcon(item, authenticateUser);

    return <TouchableOpacityWithMiddleClick
        style={styles.notificationCard}
        onPress={() => {
            setIsModalVisible(false);
            centralAPI.deleteOwnNotification(item.id).then(() => refetch());
            navigateToDestination(destination, navigation);
        }}
    >
        <Hoverable>
            {({ hovered }) => (
                <View style={{ flexDirection: 'row', gap: 12, flex: 1, }}>
                    <View style={{
                        height: 40,
                        width: 40,
                        padding: 8,
                    }}>
                        {icon
                            ? <img src={icon as string} style={{
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'left center',
                            }} />
                            : <FontAwesomeIcon icon={faLayerGroup} size='3x' color={theme.colors.iconColor} />
                        }
                    </View>
                    <View style={{ flex: 1, }}>
                        <MyAppText style={styles.notificationText}>{notification.text(item)}</MyAppText>
                        <MyAppText style={styles.notificationDate}>{moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
                    </View>
                    <View style={styles.dot(hovered)} />
                </View>
            )}
        </Hoverable>
    </TouchableOpacityWithMiddleClick>;
}

export default function NotificationModal({ navigation, isModalVisible, setIsModalVisible, notifications, refetch }: NotificationModalParameters) {
    const { styles } = useStyles(styleSheet);
    const insets = useSafeAreaInsets();
    const windowInfo = WindowInformation();

    return (
        <Modal
            visible={isModalVisible}
            animationType={windowInfo.isMobile ? 'fade' : 'none'}
            onRequestClose={() => setIsModalVisible(false)}
            transparent={true}
        >
            <Pressable onPress={() => setIsModalVisible(false)} style={styles.modalContainer(insets, windowInfo)} >
                <View style={styles.modalBody(insets, windowInfo)}>
                    <View style={styles.header}>
                        <MyAppText style={styles.titleText}>{translate('notifications')}</MyAppText>
                    </View>
                    {notifications.length > 0
                        ? <FlatList
                            style={{ paddingHorizontal: 16, paddingBottom: 8 }}
                            contentContainerStyle={{ gap: 12 }}
                            data={notifications}
                            renderItem={(item) => <NotificationCard item={item.item} navigation={navigation} setIsModalVisible={setIsModalVisible} refetch={refetch} />}
                        />
                        : <View style={{
                            paddingTop: 16,
                            paddingHorizontal: 32,
                        }}><MyAppText style={styles.noNotificationText}>{translate('noNotifications')}</MyAppText></View>
                    }
                </View>
            </Pressable>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    modalContainer: (insets: EdgeInsets, windowInfo: { isMobile: boolean; }) => ({
        flexDirection: 'column',
        flex: 1,
        padding: windowInfo.isMobile ? 16 : undefined,
        paddingTop: windowInfo.isMobile ? 66 + insets.top : undefined,
        backgroundColor: windowInfo.isMobile ? '#31313199' : undefined,
    }),
    modalBody: (insets: EdgeInsets, windowInfo: { isMobile: boolean; }) => ({
        backgroundColor: theme.colors.card.background,
        borderColor: theme.colors.card.border,
        boxShadow: '-1px 2px 16px 0px rgba(0, 0, 0, 0.16)',
        borderWidth: 1,
        flexDirection: 'column',
        borderRadius: 8,
        paddingVertical: 16,
        position: windowInfo.isMobile ? undefined : 'absolute',
        top: windowInfo.isMobile ? undefined : 55 + insets.top,
        right: windowInfo.isMobile ? undefined : 5 + insets.right,
        flex: 1,
        maxWidth: 427,
        maxHeight: windowInfo.isMobile ? 456 : 621,
    }),
    header: {
        paddingHorizontal: 16,
        paddingBottom: 16,
    },
    dot: (hovered: boolean) => ({
        width: 10,
        height: 10,
        borderRadius: 10,
        backgroundColor: hovered ? theme.colors.primaryButton.hoverBackground : theme.colors.primaryButton.background,
    }),
    notificationCard: {
        backgroundColor: theme.colors.backgrounds['BG-2'],
        padding: 12,
        borderColor: theme.colors.neutral[200],
        borderRadius: 6,
        borderWidth: 1,
    },
    noNotificationText: {
        color: theme.colors.neutral[400],
        fontWeight: '600',
        fontSize: 14,
    },
    notificationText: {
        color: theme.colors.neutral[500],
        fontWeight: '600',
        fontSize: 14,
    },
    notificationDate: {
        color: theme.colors.neutral[400],
        fontWeight: '600',
        fontSize: 12,
    },
    titleText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: theme.colors.neutral[500],
    },
}));
